import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Layers, CheckCircle2, AlertTriangle, Percent, Globe2, MapPin, BookOpen,
  Search, Filter, Check, X,
} from "lucide-react";

const KPI_META = [
  { key: "total_projects",   label: "Total Projects",    Icon: Layers,        tone: "ink" },
  { key: "on_track",         label: "On Track",          Icon: CheckCircle2,  tone: "ontrack" },
  { key: "behind",           label: "Behind",            Icon: AlertTriangle, tone: "behind" },
  { key: "avg_progress",     label: "Avg Progress",      Icon: Percent,       tone: "ink", pct: true },
  { key: "countries",        label: "Countries",         Icon: Globe2,        tone: "ink" },
  { key: "active_countries", label: "Active Countries",  Icon: MapPin,        tone: "ontrack" },
];

const TONE = {
  ontrack: "var(--on-track)", behind: "var(--behind)", ink: "var(--ink)", accent: "var(--accent)",
};

export default function Projects() {
  const [dash, setDash] = useState(null);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [colFilters, setColFilters] = useState({});

  useEffect(() => { api.get("/projects/dashboard").then((r) => setDash(r.data)); }, []);
  useEffect(() => {
    api.get("/language-projects", { params: search ? { search } : {} }).then((r) => setProjects(r.data));
  }, [search]);

  const uniques = useMemo(() => {
    const u = { region: new Set(), country: new Set(), cluster: new Set(), status: new Set() };
    for (const p of projects) {
      if (p.region_name) u.region.add(p.region_name);
      if (p.country_name) u.country.add(p.country_name);
      if (p.cluster_name) u.cluster.add(p.cluster_name);
      u.status.add(p.status?.on_track ? "On Track" : "Behind");
    }
    return Object.fromEntries(Object.entries(u).map(([k, v]) => [k, [...v].sort()]));
  }, [projects]);

  const rows = useMemo(() => projects.filter((p) => {
    for (const [k, v] of Object.entries(colFilters)) {
      if (!v?.length) continue;
      const cell =
        k === "region" ? p.region_name :
        k === "country" ? p.country_name :
        k === "cluster" ? p.cluster_name :
        k === "status" ? (p.status?.on_track ? "On Track" : "Behind") : "";
      if (!v.includes(cell)) return false;
    }
    return true;
  }), [projects, colFilters]);

  if (!dash) return <div className="p-10 mono text-xs uppercase tracking-widest">Loading dashboard…</div>;
  const k = dash.kpis;

  return (
    <div className="max-w-[1900px] mx-auto px-10 py-10 fade-in-up" data-testid="pm-dashboard">
      <div className="mb-8">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Project Management · From Project Database
        </div>
        <h1 className="serif text-5xl tracking-tight leading-none">Project Progress</h1>
        <p className="text-sm text-[color:var(--ink-2)] mt-3 max-w-2xl">
          KPIs and coverage drawn from the language-project DB. The list below is filterable Excel-style,
          and every language links through to a detail view backed by the language database.
        </p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-3 gap-2 mb-6">
        {KPI_META.map((meta) => (
          <KpiPill key={meta.key} {...meta} value={meta.pct ? `${Math.round((k[meta.key] || 0) * 100)}%` : (k[meta.key] ?? 0).toLocaleString()} />
        ))}
      </div>

      {/* Distribution wall */}
      <div className="hairline bg-white p-6 grid grid-cols-3 gap-8 mb-8">
        <DistCol title="All Projects"  data={dash.distributions.all}      color="var(--ink-2)" />
        <DistCol title="On Track"      data={dash.distributions.on_track} color="var(--on-track)" />
        <DistCol title="Behind"        data={dash.distributions.behind}   color="var(--behind)" />
      </div>

      {/* Filterable list */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-64 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <input
            className="input-flat pl-9"
            placeholder="Search language name or ISO…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="pm-search"
          />
        </div>
        <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => { setColFilters({}); setSearch(""); }}>
          <Filter size={12} /> Clear filters
        </button>
      </div>

      <div className="mono text-[11px] text-[color:var(--ink-2)] mb-3 uppercase tracking-widest">
        {rows.length} of {projects.length} projects
      </div>

      <div className="hairline bg-white overflow-x-auto">
        <table className="w-full data-table min-w-[1200px]">
          <thead>
            <tr>
              <th>Language</th>
              <th>ISO</th>
              <HeaderFilter label="Region"  values={uniques.region}  filters={colFilters} setFilters={setColFilters} col="region" />
              <HeaderFilter label="Country" values={uniques.country} filters={colFilters} setFilters={setColFilters} col="country" />
              <HeaderFilter label="Cluster" values={uniques.cluster} filters={colFilters} setFilters={setColFilters} col="cluster" />
              <HeaderFilter label="Status"  values={uniques.status}  filters={colFilters} setFilters={setColFilters} col="status" />
              <th className="text-right">Progress</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id}>
                <td className="font-medium">
                  <Link to={`/projects/${p.id}`} className="hover:underline" data-testid={`row-${p.id}`}>
                    {p.name}
                  </Link>
                </td>
                <td className="mono text-xs">{p.iso_code || "—"}</td>
                <td className="text-sm">{p.region_name}</td>
                <td className="text-sm">{p.country_name}</td>
                <td className="text-sm">{p.cluster_name}</td>
                <td>
                  <span className="chip" style={{ borderColor: p.status?.on_track ? "var(--on-track)" : "var(--behind)", color: p.status?.on_track ? "var(--on-track)" : "var(--behind)" }}>
                    {p.status?.on_track ? "On Track" : "Behind"}
                  </span>
                </td>
                <td className="text-right mono">{Math.round((p.overall || 0) * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiPill({ label, value, Icon, tone }) {
  return (
    <div className="hairline bg-white p-5" data-testid={`pm-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">{label}</div>
        <Icon size={15} className="text-[color:var(--ink-3)]" strokeWidth={1.5} />
      </div>
      <div className="serif text-4xl tracking-tight" style={{ color: TONE[tone] }}>{value}</div>
    </div>
  );
}

function DistCol({ title, data, color }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div>
      <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-3">{title}</div>
      {data.length === 0 && <div className="text-xs text-[color:var(--ink-3)] italic">No entries</div>}
      {data.slice(0, 10).map((d) => {
        const pct = Math.max(2, Math.round((d.count / max) * 100));
        return (
          <div key={d.country} className="mb-1.5">
            <div className="flex justify-between text-xs mb-0.5">
              <span className="truncate pr-2">{d.country}</span>
              <span className="mono text-[color:var(--ink-3)]">{d.count}</span>
            </div>
            <div className="h-2 bg-[color:var(--surface-2)]">
              <div className="h-full" style={{ width: `${pct}%`, background: color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HeaderFilter({ label, values, filters, setFilters, col }) {
  const active = filters[col] || [];
  const [q, setQ] = useState("");
  const [checked, setChecked] = useState(new Set(active));
  const filtered = (values || []).filter((v) => (v || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => setChecked((s) => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });

  return (
    <th className="!py-2">
      <div className="inline-flex items-center gap-1">
        <span>{label}</span>
        <Popover onOpenChange={(o) => { if (o) { setChecked(new Set(active)); setQ(""); } }}>
          <PopoverTrigger asChild>
            <button
              className={`p-0.5 border transition ${active.length ? "border-[color:var(--accent)] text-[color:var(--accent)]" : "border-transparent text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"}`}
              title="Filter"
            >
              <Filter size={10} strokeWidth={active.length ? 2.5 : 1.5} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 p-2" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)] px-1 pb-2">Filter · {label}</div>
            <div className="relative">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
              <input className="input-flat text-xs py-1 pl-7 w-full" placeholder="Search values…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="max-h-52 overflow-y-auto mt-2">
              {filtered.length === 0 && <div className="text-xs text-[color:var(--ink-3)] italic px-2 py-3">No matches</div>}
              {filtered.map((v) => (
                <label key={v} className="flex items-center gap-2 px-1.5 py-1 text-xs cursor-pointer hover:bg-[color:var(--surface-2)]">
                  <input type="checkbox" className="accent-[color:var(--ink)]" checked={checked.has(v)} onChange={() => toggle(v)} />
                  <span className="truncate flex-1" title={v}>{v}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-between gap-2 pt-2 border-t border-[color:var(--border)] mt-2">
              <button className="btn-ghost text-xs" onClick={() => { setChecked(new Set()); setFilters({ ...filters, [col]: [] }); }}>Clear</button>
              <button className="btn-primary text-xs" onClick={() => setFilters({ ...filters, [col]: [...checked] })}>
                <Check size={11} className="inline mr-1" />Apply
              </button>
            </div>
          </PopoverContent>
        </Popover>
        {active.length > 0 && (
          <span className="mono text-[9px] px-1.5 py-0.5 rounded-sm cursor-pointer"
            style={{ background: "rgba(107,111,150,0.12)", color: "var(--accent)" }}
            onClick={() => setFilters({ ...filters, [col]: [] })}>
            {active.length}<X size={9} className="inline ml-0.5" />
          </span>
        )}
      </div>
    </th>
  );
}
